import {
  Application,
  ApplicationConfig,
  ApplicationConfigFieldGroup,
  ApplicationConfigRaw,
  ApplicationConfigStep,
  ApplicationData,
  ApplicationPartialConfig,
  ApplicationPartialConfigGroupOverride,
  ApplicationPartialConfigStepOverride,
  ApplicationStep,
  ApplicationStepState,
  ClientTypeType,
  Proposal,
  ProposalStepCustomizer,
  QuoteType,
} from '@lockton-apps/shared/interfaces';
import {
  cloneDeep,
  compact,
  defaults,
  defaultsDeep,
  filter,
  find,
  findIndex,
  forEach,
  get,
  includes,
  isNumber,
  isPlainObject,
  isUndefined,
  kebabCase,
  map,
  orderBy,
  reduce,
  some,
  split,
  transform,
} from 'lodash';
import { buildPermissions } from './proposal-permissions';
import { Injectable } from '@angular/core';
import {
  ClassOfBusinessId,
  CobMeta,
  getClassOfBusinessMeta,
} from '@lockton-apps/shared/util/constants';

@Injectable({ providedIn: 'root' })
export class ProposalBuilderService {
  initializeProposal(
    proposal: Proposal,
    config: ApplicationConfig,
    stepCustomizer: ProposalStepCustomizer,
    clientType: ClientTypeType,
    isSubmitted: boolean,
    quoteType: QuoteType
  ): Proposal {
    return defaultsDeep(
      {
        formData: {
          initialized: true,
          completedStep: proposal.formData.initialized
            ? proposal.formData.completedStep
            : null,
          version: config.version,
          steps: this.buildSteps(
            proposal.formData.steps,
            config.steps as ApplicationConfigStep[],
            proposal.selectedProducts,
            proposal.formData.addons || [],
            stepCustomizer,
            clientType,
            isSubmitted,
            quoteType
          ),
          data: proposal.formData.data,
          permissions: buildPermissions(config.steps),
        },
      },
      proposal
    );
  }

  updateProposal(
    proposal: Proposal,
    completedStepIndex: number,
    linearProgress: boolean,
    affectedStepsIds: Set<string>
  ): Proposal {
    const application: Application = defaults(
      {
        completedStep: completedStepIndex,
        steps: proposal.formData
          ? map(
              proposal.formData.steps,
              (step: ApplicationStep, index: number) => ({
                ...step,
                state: linearProgress
                  ? this.getLinearStepState(
                      step,
                      index,
                      completedStepIndex,
                      affectedStepsIds
                    )
                  : this.getNonLinearStepState(
                      step,
                      index,
                      completedStepIndex,
                      affectedStepsIds
                    ),
              })
            )
          : [],
      },
      proposal.formData
    );

    return defaults({ formData: application }, proposal);
  }

  buildConfig(
    mainConfig: ApplicationConfigRaw,
    partials: Record<string, ApplicationPartialConfig>,
    clientType: ClientTypeType | undefined
  ): ApplicationConfig {
    const config: ApplicationConfig = this.addSteps(
      cloneDeep(mainConfig),
      partials
    );

    this.addGroups(config, partials);
    this.addTemplates(config, partials);
    this.cleanUp(config, clientType);

    return config;
  }

  buildSteps(
    proposalSteps: ApplicationStep[],
    configSteps: ApplicationConfigStep[],
    selectedProducts: ClassOfBusinessId[],
    addonIds: string[],
    customizer: ProposalStepCustomizer,
    clientType: ClientTypeType,
    isSubmitted: boolean,
    quoteType?: QuoteType
  ): ApplicationStep[] {
    const productMetaToRenewal: CobMeta[] = compact(
      map(selectedProducts, getClassOfBusinessMeta)
    );

    const dueToRenewal: ApplicationConfigStep[] = filter(configSteps, (step) =>
      some(productMetaToRenewal, { id: step.id })
    );

    const productPicker: ApplicationConfigStep | undefined = find(configSteps, {
      productPicker: true,
    });

    const addons: ApplicationConfigStep[] = filter(
      configSteps,
      (step: ApplicationStep) => includes(addonIds, step.id)
    ) as ApplicationConfigStep[];

    const mandatory: ApplicationConfigStep[] = filter(
      configSteps,
      (step) =>
        step.mandatory &&
        (!isNumber(step.clientType) || step.clientType === clientType) &&
        (!step.quoteType || includes(step.quoteType, quoteType))
    );

    return map(
      customizer(dueToRenewal, productPicker, addons, mandatory),
      (step: ApplicationConfigStep, index: number) => {
        let state: ApplicationStepState;
        const existingStep: ApplicationStep | undefined = find(proposalSteps, {
          id: step.id,
        });

        switch (true) {
          case !!existingStep?.state:
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            state = existingStep.state;
            break;

          case index && !isSubmitted:
            state = ApplicationStepState.Uncompleted;
            break;

          case !index && !isSubmitted:
            state = ApplicationStepState.InProgress;
            break;

          default:
            state = ApplicationStepState.Completed;
        }

        return {
          id: step.id,
          state,
        };
      }
    );
  }

  rebuildSteps(
    proposalSteps: ApplicationStep[],
    configSteps: ApplicationConfigStep[],
    selectedProducts: ClassOfBusinessId[],
    model: ApplicationData,
    addonIds: string[],
    stepCustomizer: ProposalStepCustomizer,
    clientType: ClientTypeType,
    quoteType?: QuoteType
  ): Partial<Application> {
    const newSteps: ApplicationStep[] = this.buildSteps(
      proposalSteps,
      configSteps,
      selectedProducts,
      addonIds,
      stepCustomizer,
      clientType,
      false,
      quoteType
    );
    const newData: ApplicationData = transform(
      cloneDeep(model),
      (result: ApplicationData, stepValue: unknown, stepName: string) => {
        if (some(newSteps, { id: kebabCase(stepName) })) {
          result[stepName] = stepValue;
        }
      }
    );

    return { steps: newSteps, data: newData };
  }

  private addSteps(
    config: ApplicationConfigRaw,
    partials: Record<string, ApplicationPartialConfig>
  ): ApplicationConfig {
    forEach(
      config.partials,
      (partial: string | ApplicationPartialConfigStepOverride) => {
        let step: ApplicationConfigStep = this.getPartialConfig(
          partial,
          partials,
          'steps'
        );

        if (this.isPartialConfigWithOverrides(partial)) {
          step = defaultsDeep(partial.overrides, step);

          this.filterFields(
            step,
            partial.omitFields,
            (isListed: boolean) => isListed
          );
          this.filterFields(
            step,
            partial.includeFields,
            (isListed: boolean) => !isListed
          );
        }

        config.steps?.push(step);
      }
    );

    return config as ApplicationConfig;
  }

  private addGroups(
    config: ApplicationConfig,
    partials: Record<string, ApplicationPartialConfig>
  ): void {
    forEach(config.steps, (step: ApplicationConfigStep) => {
      forEach(
        step.partials,
        (partial: string | ApplicationPartialConfigGroupOverride) => {
          const partialGroupConfig = this.getPartialConfig(
            partial,
            partials,
            'groups'
          );
          const group: ApplicationConfigFieldGroup =
            this.isPartialConfigWithOverrides(partial)
              ? defaultsDeep(partial.overrides, partialGroupConfig)
              : partialGroupConfig;

          step.fieldGroups.push(group);
        }
      );

      step.fieldGroups = orderBy(step.fieldGroups, 'indexOverride');
    });
  }

  private addTemplates(
    config: ApplicationConfig,
    partials: Record<string, ApplicationPartialConfig>
  ): void {
    const partialTemplates = reduce(
      partials,
      (acc, cur) => {
        return { ...acc, ...cur.templates };
      },
      {} as Record<string, ApplicationConfigStep>
    );

    const mergedTemplates = defaultsDeep(
      { ...config.templates },
      partialTemplates
    );
    config.templates = mergedTemplates;
  }

  private cleanUp(
    config: ApplicationConfig,
    clientType: ClientTypeType | undefined
  ): void {
    config.steps = orderBy(
      filter(config.steps, (step) => {
        if (step.id) {
          step.fieldGroups = orderBy(
            filter(step.fieldGroups, (fieldGroup) => {
              const stepMatch: boolean = !!fieldGroup.id;
              const clientTypeMatch: boolean =
                isUndefined(fieldGroup.clientType) ||
                fieldGroup.clientType === clientType;

              return stepMatch && clientTypeMatch;
            }),
            'indexOverride'
          );

          return true;
        }

        return false;
      }),
      'indexOverride'
    );
  }

  private getPartialConfig(
    partial:
      | string
      | ApplicationPartialConfigStepOverride
      | ApplicationPartialConfigGroupOverride,
    partials: Record<string, ApplicationPartialConfig>,
    target: 'steps' | 'groups'
  ) {
    const [partialConfigName, partialStepName] = split(
      this.isPartialConfigWithOverrides(partial) ? partial.id : partial,
      ':'
    );

    return cloneDeep(
      get(partials, [partialConfigName, target, partialStepName])
    );
  }

  private isPartialConfigWithOverrides(
    partial: unknown
  ): partial is
    | ApplicationPartialConfigStepOverride
    | ApplicationPartialConfigGroupOverride {
    return isPlainObject(partial);
  }

  private getLinearStepState(
    step: ApplicationStep,
    index: number,
    completedStepIndex: number,
    affectedStepsIds: Set<string>
  ): ApplicationStepState {
    switch (true) {
      case step.state !== ApplicationStepState.Uncompleted &&
        affectedStepsIds.has(step.id):
        return ApplicationStepState.Invalid;

      case index === completedStepIndex:
      case index <= completedStepIndex &&
        step.state !== ApplicationStepState.Invalid:
        return ApplicationStepState.Completed;

      case index === completedStepIndex + 1 &&
        step.state === ApplicationStepState.Uncompleted:
        return ApplicationStepState.InProgress;

      default:
        return step.state;
    }
  }

  private getNonLinearStepState(
    step: ApplicationStep,
    index: number,
    completedStepIndex: number,
    affectedStepsIds: Set<string>
  ): ApplicationStepState {
    switch (true) {
      case step.state !== ApplicationStepState.Uncompleted &&
        affectedStepsIds.has(step.id):
        return ApplicationStepState.Invalid;

      case index === completedStepIndex:
        return ApplicationStepState.Completed;

      default:
        return step.state;
    }
  }

  private filterFields(
    step: ApplicationConfigStep,
    fields: Record<string, string[]> | undefined,
    toOmit: (isListed: boolean) => boolean
  ): void {
    forEach(fields, (fieldKeys: string[], groupId: string) => {
      const groupIndex: number = findIndex(step.fieldGroups, {
        id: groupId,
      });

      forEach(
        step.fieldGroups[groupIndex].fields,
        (field: any, index: number) => {
          const isListed: boolean = includes(fieldKeys, field.key);

          if (toOmit(isListed)) {
            delete step.fieldGroups[groupIndex].fields[index];
          }
        }
      );

      step.fieldGroups[groupIndex].fields = compact(
        step.fieldGroups[groupIndex].fields
      );
    });
  }
}
